import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { FEATURED } from '../content/mediaLibrary';
import useSEO from '../hooks/useSEO';

const HOURS = [
  { key: 'mon', time: '10h – 19h' },
  { key: 'tue', time: '10h – 19h' },
  { key: 'wed', time: '10h – 19h' },
  { key: 'thu', time: '10h – 20h' },
  { key: 'fri', time: '10h – 20h' },
  { key: 'sat', time: '9h – 16h' },
  { key: 'sun', time: '9h – 16h' },
];

const ADDRESS_QUERY = encodeURIComponent(
  '56 Rue Jean-Talon Ouest, Montréal, QC H2R 2W7'
);
const MAPS_EMBED = `https://www.google.com/maps?q=${ADDRESS_QUERY}&output=embed`;
const MAPS_URL = `https://www.google.com/maps/search/?api=1&query=${ADDRESS_QUERY}`;

const ContactPage = () => {
  const { t } = useLanguage();
  const c = t.contact;
  useSEO({ title: t.seo.contact.title, description: t.seo.contact.description });

  return (
    <div className="min-h-screen bg-[#F5F0E8] pt-24 lg:pt-32" data-testid="contact-page">
      {/* HERO INTRO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="deco-line" />
            <span className="eyebrow text-[#8A8780]" data-testid="contact-eyebrow">
              {c.eyebrow}
            </span>
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-7xl font-normal tracking-tight text-[#1A1A18] leading-[1.02] mb-5"
            data-testid="contact-title"
          >
            {c.title}
          </h1>
          <p className="font-display italic text-lg lg:text-2xl text-[#1A1A18]/60 max-w-xl" style={{ lineHeight: 1.5 }}>
            {c.intro}
          </p>
        </motion.div>
      </section>

      {/* SPLIT CONTENT: map/photo left, details right */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Left — visual column */}
          <div className="lg:col-span-7 space-y-4">
            {/* Google Map */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden border border-[#8A8780]/25 bg-[#1A1A18]"
              data-testid="contact-map"
            >
              <iframe
                src={MAPS_EMBED}
                width="100%"
                height="420"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Casanova Salon Barbier — Google Maps"
              />
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-[#A14223] text-[#F5F0E8] px-4 py-2 text-xs tracking-[0.2em] uppercase hover:bg-[#8d3a1e] transition-colors font-body shadow-lg"
                data-testid="contact-map-cta"
              >
                {c.directionsCta}
                <ArrowUpRight size={14} />
              </a>
            </motion.div>

            {/* Storefront polaroid */}
            <motion.figure
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="relative inline-block w-full"
              data-testid="contact-storefront"
            >
              <div className="relative aspect-[16/10] overflow-hidden border border-[#8A8780]/25 bg-[#1A1A18]">
                <img
                  src={`https://images.weserv.nl/?url=${encodeURIComponent(
                    FEATURED.contactStorefront.replace(/^https?:\/\//, '')
                  )}&w=1600&output=webp&q=90&we`}
                  alt={c.storefrontCaption}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  style={{ filter: 'contrast(1.05) saturate(0.96) brightness(0.98)' }}
                />
              </div>
              <figcaption className="mt-3 font-display italic text-sm text-[#1A1A18]/55 max-w-md">
                {c.storefrontCaption}
              </figcaption>
            </motion.figure>
          </div>

          {/* Right — details column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 space-y-8"
          >
            {/* Address card */}
            <InfoCard
              icon={<MapPin size={18} />}
              label={c.addressLabel}
              testid="contact-address"
            >
              <p className="font-display text-2xl text-[#1A1A18] leading-tight">
                {c.addressLine1}
              </p>
              <p className="font-body text-sm text-[#1A1A18]/60 tracking-[0.08em] mt-1">
                {c.addressLine2}
              </p>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 eyebrow text-[#A14223] hover:text-[#8d3a1e] transition-colors"
              >
                {c.directions}
                <ArrowUpRight size={12} />
              </a>
            </InfoCard>

            {/* Phone / Email cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a
                href="tel:+15142272366"
                className="block p-5 bg-[#25342C] text-[#F5F0E8] hover:bg-[#1E2B24] transition-colors group"
                data-testid="contact-phone"
              >
                <div className="flex items-center gap-2 mb-3 text-[#F5F0E8]/70">
                  <Phone size={16} />
                  <span className="eyebrow text-[#F5F0E8]/70 text-[10px]">{c.phoneLabel}</span>
                </div>
                <div className="font-display text-xl">{c.phone}</div>
                <div className="mt-3 eyebrow text-[10px] text-[#A14223] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                  {c.callCta} <ArrowUpRight size={12} />
                </div>
              </a>

              <a
                href={`mailto:${c.email}`}
                className="block p-5 bg-[#A14223] text-[#F5F0E8] hover:bg-[#8d3a1e] transition-colors group"
                data-testid="contact-email"
              >
                <div className="flex items-center gap-2 mb-3 text-[#F5F0E8]/80">
                  <Mail size={16} />
                  <span className="eyebrow text-[#F5F0E8]/80 text-[10px]">{c.emailLabel}</span>
                </div>
                <div className="font-display text-base break-all leading-tight">{c.email}</div>
                <div className="mt-3 eyebrow text-[10px] text-[#F5F0E8]/80 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                  {c.emailCta} <ArrowUpRight size={12} />
                </div>
              </a>
            </div>

            {/* Hours */}
            <InfoCard
              icon={<Clock size={18} />}
              label={c.hoursLabel}
              testid="contact-hours"
            >
              <ul className="space-y-2 mt-1">
                {HOURS.map(({ key, time }) => (
                  <li
                    key={key}
                    className="flex items-baseline justify-between gap-4 py-2 border-b border-[#8A8780]/15 last:border-b-0"
                  >
                    <span className="font-body text-sm text-[#1A1A18]/70 tracking-wide">
                      {c.days[key]}
                    </span>
                    <span className="font-display text-base text-[#1A1A18]">{time}</span>
                  </li>
                ))}
              </ul>
            </InfoCard>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const InfoCard = ({ icon, label, children, testid }) => (
  <div className="bg-[#F5F0E8] border border-[#8A8780]/25 p-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3 text-[#8A8780]">
      {icon}
      <span className="eyebrow text-[#8A8780] text-[10px]">{label}</span>
    </div>
    {children}
  </div>
);

export default ContactPage;
